import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { fetchOfficeChecklist, fetchOffices } from '~/data/interact'
import { theme } from '~/theme'

// The office (box) health card, in-game: the live onboarding checklist that
// the server rack's LEDs summarize — every step with its status, what it
// means, and the exact copy-paste commands to fix it on the box. No dashboard
// round-trip needed.

const STATUS_META: Record<string, { color: string; glyph: string }> = {
  OK: { color: '#54d18c', glyph: '✓' },
  WARN: { color: '#e0a13a', glyph: '!' },
  ACTION: { color: '#ff5a5a', glyph: '✗' },
  PENDING: { color: '#8b93a7', glyph: '…' },
}

export function OfficePanel({
  officeId,
  organizationId,
  getToken,
  onClose,
  compact,
}: {
  officeId: string
  organizationId: string
  getToken: GetToken
  onClose: () => void
  compact?: boolean
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const client = async () => createClient(await getToken())

  const offices = useQuery({
    queryKey: ['offices', organizationId],
    queryFn: async () => fetchOffices(await client(), organizationId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const office = (offices.data ?? []).find((o) => o.id === officeId)

  const checklist = useQuery({
    queryKey: ['officeChecklist', officeId],
    queryFn: async () => fetchOfficeChecklist(await client(), officeId),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const steps = checklist.data?.steps ?? []
  const worst = steps.some((s) => s.status === 'ACTION')
    ? 'ACTION'
    : steps.some((s) => s.status === 'WARN')
      ? 'WARN'
      : 'OK'
  const worstMeta = STATUS_META[worst]

  const copy = (key: string, commands: string[]): void => {
    void navigator.clipboard.writeText(commands.join('\n')).then(() => {
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    })
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.cream, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🖥 {office?.name ?? 'Office'}
          </div>
          <div style={{ fontSize: 11, color: worstMeta.color, fontWeight: 600 }}>
            {worst === 'OK'
              ? 'healthy — everything checks out'
              : worst === 'WARN'
                ? 'check setup — something needs a look'
                : 'needs attention — the box can’t work properly'}
          </div>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
      </div>

      {checklist.isLoading && <div style={dim}>Running the checklist…</div>}
      {checklist.isError && (
        <div style={dim}>Checklist isn't visible — org owners only.</div>
      )}

      {steps.map((s) => {
        const meta = STATUS_META[s.status] ?? STATUS_META.PENDING
        return (
          <div key={s.key} style={stepBox}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span
                style={{
                  color: meta.color,
                  fontWeight: 800,
                  fontSize: 12,
                  width: 14,
                  textAlign: 'center',
                  flex: '0 0 auto',
                }}
              >
                {meta.glyph}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: theme.cream, flex: 1 }}>
                {s.title}
              </span>
              <span style={{ fontSize: 10, color: meta.color, fontWeight: 700 }}>
                {s.status.toLowerCase()}
              </span>
            </div>
            {s.detail && (
              <div style={{ fontSize: 11.5, color: theme.textDim, marginTop: 3, paddingLeft: 22, lineHeight: 1.45 }}>
                {s.detail}
              </div>
            )}
            {s.commands.length > 0 && (
              <div style={{ marginTop: 6, marginLeft: 22 }}>
                <div style={cmdBox}>
                  {s.commands.map((c, i) => (
                    <div key={i}>$ {c}</div>
                  ))}
                </div>
                <button onClick={() => copy(s.key, s.commands)} style={copyBtn}>
                  {copiedKey === s.key ? 'Copied ✓' : 'Copy commands'}
                </button>
              </div>
            )}
          </div>
        )
      })}

      {!checklist.isLoading && steps.length > 0 && (
        <div style={{ fontSize: 10, color: theme.textDim, marginTop: 8, textAlign: 'center' }}>
          Live checklist · refreshes every 30s
        </div>
      )}
    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 340,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 14,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const dim: React.CSSProperties = { fontSize: 11.5, color: theme.textDim, padding: '10px 0' }
const stepBox: React.CSSProperties = {
  marginTop: 8,
  padding: '8px 9px',
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
}
const cmdBox: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 10.5,
  lineHeight: 1.6,
  color: '#7ff0ff',
  background: '#0b0d12',
  border: `1px solid ${theme.border}`,
  borderRadius: 7,
  padding: '6px 8px',
  overflowX: 'auto',
  whiteSpace: 'pre',
}
const copyBtn: React.CSSProperties = {
  marginTop: 5,
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 7,
  fontSize: 10.5,
  padding: '4px 9px',
  cursor: 'pointer',
}
